import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| TYPES ||============================== //

// Review iteration for grouping multiple reviews of the same PR (nested items)
export interface ReviewIterationItem {
  id: string;
  status: string;
  reviewEvent: string;
  workflowType: string;
  hasChildPR: boolean;
  childPrNumber: number | null;
  childPrUrl: string | null;
  childPrBranch: string | null;
  childPrStatus: string | null;
  acuUsed: number | null;
  commentProcessingACU: number | null;
  feedbackIterations: number;
  createdAt: string;
  postedAt: string | null;
}

// Child review item for human_pr workflow (existing grouping)
export interface ChildReviewItem {
  id: string;
  childPrNumber: number | null;
  childPrUrl: string | null;
  childPrBranch: string | null;
  childPrStatus: string | null;
  acuUsed: number | null;
  feedbackIterations: number;
  createdAt: string;
  status: string;
}

// Agent work item (with grouping fields from backend)
export interface RepositoryAgentWorkItem {
  id: string;
  prNumber: number;
  prUrl: string;
  prTitle: string;
  prAuthor: string;
  status: string;
  reviewEvent: string;
  workflowType: string;
  hasChildPR: boolean;
  childPrNumber: number | null;
  childPrUrl: string | null;
  childPrBranch: string | null;
  childPrStatus: string | null;
  ticketId: string | null;
  ticketSource: string | null;
  ticketUrl: string | null;
  linesChanged: number;
  tokensUsed: number | null;
  acuUsed: number | null;
  commentProcessingACU: number | null;  // Separate bucket for comment classification costs
  feedbackIterations: number;
  createdAt: string;
  postedAt: string | null;
  // Grouping fields for human_pr workflow (child PRs)
  isGrouped?: boolean;
  childReviewCount?: number;
  childReviews?: ChildReviewItem[];
  // Grouping fields for review iterations (multiple reviews of same PR)
  hasMultipleReviews?: boolean;
  reviewIterationCount?: number;
  reviewIterations?: ReviewIterationItem[];
}

interface RepositoryAgentWorkState {
  agentWork: RepositoryAgentWorkItem[];
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: RepositoryAgentWorkState = {
  agentWork: [],
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getRepositoryAgentWork = createAsyncThunk(
  'repositoryAgentWork/getRepositoryAgentWork',
  async (repositoryId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/repositories/${repositoryId}/agent-work`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| SLICE ||============================== //

export const repositoryAgentWorkSlice = createSlice({
  name: 'repositoryAgentWork',
  initialState,
  reducers: {
    clearAgentWork(state) {
      state.agentWork = [];
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRepositoryAgentWork.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getRepositoryAgentWork.fulfilled, (state, action) => {
        const data = action.payload?.response || action.payload;
        state.agentWork = Array.isArray(data) ? data : [];
        state.status = 'success';
        state.error = '';
      })
      .addCase(getRepositoryAgentWork.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch agent work';
      });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearAgentWork } = repositoryAgentWorkSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectRepositoryAgentWork = (state: any) => state.repositoryAgentWork.agentWork;
export const selectRepositoryAgentWorkStatus = (state: any) => state.repositoryAgentWork.status;
export const selectRepositoryAgentWorkError = (state: any) => state.repositoryAgentWork.error;

export default repositoryAgentWorkSlice.reducer;
